<template>
  <div class="mux-video-player">
    <v-alert
      v-if="error"
      type="error"
      variant="tonal"
      density="comfortable"
      class="mb-3"
    >
      {{ error }}
    </v-alert>

    <div
      v-if="loading"
      class="mux-video-player__loading"
    >
      <v-progress-circular
        indeterminate
        color="primary"
      />
      <span class="text-body-2 text-medium-emphasis">Loading video...</span>
    </div>

    <mux-player
      ref="playerRef"
      v-else-if="descriptor"
      class="mux-video-player__player"
      stream-type="on-demand"
      :playback-id="descriptor.playbackId"
      :playback-token="descriptor.tokens?.['playback-token']"
      :thumbnail-token="descriptor.tokens?.['thumbnail-token']"
      :storyboard-token="descriptor.tokens?.['storyboard-token']"
      :metadata-video-id="descriptor.metadata?.video_id"
      :metadata-video-title="descriptor.metadata?.video_title"
      :metadata-viewer-user-id="descriptor.metadata?.viewer_user_id"
      :env-key="descriptor.metadata?.env_key"
      :autoplay="autoplay || undefined"
      :muted="muted || undefined"
      :poster="poster"
      @play="emit('play')"
    />
  </div>
</template>

<script setup>
import "@mux/mux-player";
import { onMounted, ref, watch } from "vue";

import { getMuxPlaybackDescriptor } from "@/services/muxVideoCatalog";

const props = defineProps({
  videoId: {
    type: String,
    default: "",
  },
  autoplay: {
    type: Boolean,
    default: false,
  },
  muted: {
    type: Boolean,
    default: false,
  },
  poster: {
    type: String,
    default: "",
  },
});

const emit = defineEmits(["play"]);
const loading = ref(false);
const error = ref("");
const descriptor = ref(null);
const playerRef = ref(null);

const loadDescriptor = async () => {
  const videoId = props.videoId?.trim();
  descriptor.value = null;
  error.value = "";

  if (!videoId) return;

  loading.value = true;
  try {
    descriptor.value = await getMuxPlaybackDescriptor({ videoId });
  } catch (err) {
    console.error("Failed to load Mux playback descriptor", err);
    error.value = err?.message || "Unable to load video.";
  } finally {
    loading.value = false;
  }
};

onMounted(loadDescriptor);
watch(() => props.videoId, loadDescriptor);

defineExpose({
  play: () => playerRef.value?.play?.(),
  pause: () => playerRef.value?.pause?.(),
});
</script>

<style scoped lang="scss">
.mux-video-player {
  width: 100%;
}

.mux-video-player__loading {
  align-items: center;
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 12px;
  display: flex;
  gap: 12px;
  min-height: 160px;
  padding: 24px;
}

.mux-video-player__player {
  aspect-ratio: 16 / 9;
  background: #000;
  border-radius: 14px;
  overflow: hidden;
  width: 100%;
}
</style>
